/**
 * pricing-type.ts — the ONLY size/weight/leading source inside the Pricing
 * section (R5-P2 design amendment §5 "PRICING — one row grammar", Decision
 * 10 [[.claude/handoffs/landing-review-artifacts/r5/r5-design-amendment.md]]
 * [ADV-26]). Mirrors `ui/tone.ts` / `ui-light/light-tokens.ts`'s discipline:
 * every consumer imports a pre-baked Tailwind-class STRING from here — never
 * hand-composes `text-[Npx] leading-[Mpx] font-*` at a call site — so there
 * is exactly one place a pricing type size can drift. The eventual gate
 * (amendment §8.4) bans raw `text-[Npx]` AND named size utilities
 * (`text-sm/base/lg/...`) inside the Pricing section scope UNLESS the class
 * comes from this registry.
 *
 * SCOPE — TYPOGRAPHY ONLY, deliberately no color: `r5-defect-map.md` §4.1's
 * census counts 10 distinct (size,weight,leading) triples across the two
 * pricing cards ("closer to 10 once case/tracking/weight are counted
 * alongside raw size") — that chaos is what this registry kills. Text
 * COLOR is a separate, per-instance/per-tier concern (Starter vs Pro,
 * popular-amber vs neutral) that must keep varying independently of type —
 * folding color in here would re-couple two orthogonal decisions. Compose
 * at the call site: `cn(PRICING_TYPE.row, DARK_INK.tertiary.text)` (R6:
 * neutral ink colors now resolve through `ui/dark-tokens.ts`'s registry
 * rather than a hand-typed `text-[#hex]` literal -- same "color composes
 * separately" split, just retinted-token-sourced instead of a raw hex).
 * This exactly
 * matches the ALREADY-SHIPPED pattern in `PricingSection.tsx`'s own
 * `LimitsTable` (`valueClass = cn('text-[20px] leading-[24px] ... ',
 * limitValueColor)`) — this file just names that pattern's SIZE half as a
 * reusable role instead of a one-off local const.
 *
 * `sectionH2` is the one role needing a font-FAMILY override (serif, not
 * Inter) — it applies the SAME shared `landing-serif-display` utility class
 * every other converted section H2 now uses (`globals.css:3085`:
 * `font-family:var(--font-landing-serif); font-weight:400;
 * font-synthesis:none; letter-spacing:normal` — family/weight/synthesis/
 * tracking ONLY; size+leading are deliberately excluded from that shared
 * rule because they differ per node, so they stay right here on this role).
 * P3 (checkpoint-1 RESOLVED 2026-07-15: Libre Caslon Text 400 won the A/B)
 * wires this role directly to that utility instead of re-deriving the same
 * four properties as raw Tailwind literals — the shared class landed this
 * same wave (Lane C0b, `next/font/google` in
 * `src/features/marketing/fonts/landing-serif.ts`), so Pricing now rides
 * the identical mechanism Hero/FAQ/FinalCTA/FeatureGrid/TrustBlock already
 * do — one utility, not six near-identical copies.
 *
 * ── ELEMENT → ROLE MAPPING TABLE (for the P3 live PricingSection.tsx
 *    conversion; census letters = r5-defect-map.md §4.1, `PricingSection.tsx`
 *    line numbers as of R4-P3) ──────────────────────────────────────────
 *
 * sectionH2 · new  · H2 section headline (`:223`, was 32/33→52/54 Inter
 *   font-semibold tracking-[-0.02em]) — BIGGEST delta in this file: shrinks
 *   to 34→40 AND swaps Inter-bold for serif-regular. Not pricing-specific —
 *   this is the global "every section H2 unifies to serif 40" law (Decision
 *   1/2, amendment §7 conformance matrix), just instantiated here.
 * A · tierName  · plan name `h3` (`:136`, `text-[18px] leading-[24px]
 *   font-semibold`) — UNCHANGED, now named/governed instead of a local literal.
 * B · (none)    · "Most popular" badge (`:138`, `text-sm font-semibold`
 *   inside a pill) — NOT one of the 8 named roles in amendment §5, by
 *   design (it is a Chip call site, not a type-ramp text node). RESOLVED IN
 *   P3 (checkpoint-1 ruling 5): renders via `<Chip variant="status">`,
 *   which owns its OWN internal 13px/[font-weight:510] pill scale in
 *   `ui/Chip.tsx` — out of THIS registry's scope by construction (Chip.tsx
 *   is a different file, owned by a different lane). The call site in
 *   `PricingSection.tsx` layers only the color trio via `className`
 *   (`border-[#F59E0B3D] bg-[#F59E0B14] text-[#FBBF24]`) — zero size
 *   literals, so it never needed a 9th role here.
 * C · row        · tagline `p` (`:143`) — UNCHANGED (16/24/400 already).
 * D · price      · price number (`:147`) — leading simplified from
 *   px-pegged (`leading-[48px] lg:leading-[56px]`, a 1:1 ratio at each
 *   breakpoint) to unitless `leading-none` (line-height:1), which is
 *   VISUALLY IDENTICAL at any breakpoint and matches the amendment's literal
 *   "1" better than a px value re-coupled per breakpoint. `tracking-[-0.02em]`
 *   is CARRIED OVER from `:147` (amendment §5's compressed "48→56/1/600"
 *   shorthand doesn't mention tracking, unlike its explicit sectionH2
 *   discussion) — kept because it is an already-shipped, unflagged, standard
 *   big-numeral tightening, not part of the "5 variants of 16px" complaint.
 * E · row        · period text (`:150`, was `text-base` — a Tailwind NAMED
 *   token, the second of the census's flagged "two mechanisms for the same
 *   16px" pair) — now the SAME arbitrary-literal role as tagline/VAT/limits
 *   label/feature-row, one mechanism.
 * F · row        · VAT label `p` (`:155-157`) — WEIGHT CHANGE:
 *   `font-semibold` (600) → `font-normal` (400, via `row`); leading 22→24.
 *   Intentional per Decision 10 "one row grammar" — flag this one visibly at
 *   P3 review, it is the one role merge that changes a currently-bold node
 *   to regular weight.
 * G · row        · limits table row label (`:85,91`) — UNCHANGED (already
 *   matched C/E's 16/24/400 before this file existed).
 * H · limitValue · limits table value (`:78`) — UNCHANGED numbers
 *   (20→24/24→28/600 tabular), now named "the one deliberate large data
 *   emphasis" (amendment §5) instead of an unregistered exception.
 * I · groupHeading · FeatureGroups heading (`:45`, was 16/22/600 uppercase
 *   tracking-[0.08em]) — SIZE/LEADING SHRINK to 13/18 (more "eyebrow"-scale,
 *   consistent with the premium-density direction elsewhere in R5); tracking
 *   value 0.08em is a CARRY-OVER (amendment says "uppercase tracked" with no
 *   number — reusing the already-tuned house value rather than inventing one).
 * J · row        · FeatureGroups row text (`:58`) — leading 22→24 (weight
 *   already 400, matched).
 * K · cta        · CTA button label (`:171`, was "exempt from the type
 *   ramp" per that file's OWN comment) — UNCHANGED 15px/600, no leading (a
 *   flex-centered button, never line-height-dependent) — now formally
 *   governed instead of exempt; numbers didn't move.
 * (none) · micro · 13/18/400 — RESOLVED IN P3: the section-level Stripe
 *   assurance line ("Secure payment through Stripe", was `:282` 14/20/400)
 *   now maps here (13/18/400, a documented 1-2px shrink) — exactly the
 *   destination this file originally forward-declared. The setup-strip
 *   promise (was `:236` 18/28/600) maps to `tierName` instead (same
 *   18px/600, a 4px leading flatten 28->24) — the closest registered role
 *   by value, per Decision 10 "one row grammar." The section subtitle
 *   (was `:226` 16/24 mobile -> lg:text-lg/lg:leading-[1.55]) maps to
 *   `row`, dropping its lg upsize entirely — `PRICING_TYPE` is "the ONLY
 *   size source inside the section scope" (amendment §5), so a surviving
 *   per-node breakpoint variant here would be exactly the un-registered
 *   exception the §8.4 gate exists to kill. Every text node in
 *   `PricingSection.tsx` now maps to one of the 8 roles above.
 *
 * Related: [[.claude/handoffs/landing-review-artifacts/r5/r5-design-amendment.md]] §5/§8.4
 * · [[.claude/handoffs/landing-review-artifacts/r5/r5-defect-map.md]] §4
 * · `ui/tone.ts` (sibling registry, sibling discipline) · `ui-light/light-tokens.ts` (serifFontVar origin)
 * · `ui/dark-tokens.ts` (R6: the ink registry `PricingSection.tsx` composes
 *   at each role's call site, per this file's own "color composes
 *   separately" discipline above)
 */

export type PricingTypeRole =
  | 'sectionH2'
  | 'tierName'
  | 'price'
  | 'row'
  | 'groupHeading'
  | 'limitValue'
  | 'micro'
  | 'cta';

export const PRICING_TYPE: Record<PricingTypeRole, string> = {
  /** Section H2 — serif display voice (Decision 1/2). Checkpoint-1
   * RESOLVED (2026-07-15): Libre Caslon Text 400 won the A/B, weight stays
   * 400 (LCT has no 500/600 anyway). Family/weight/font-synthesis/tracking
   * route through the shared `landing-serif-display` utility class
   * (globals.css:3085) — the SAME class every other converted section H2
   * (Hero/FAQ/FinalCTA/FeatureGrid/TrustBlock) now applies — this role
   * owns only the two properties that differ per node: size (34px mobile
   * -> 40px lg) and leading (1.15 -> 1.1). */
  sectionH2: 'landing-serif-display text-[34px] leading-[1.15] lg:text-[40px] lg:leading-[1.1]',

  /** Tier name (`h3`, "Starter"/"Pro") — Inter interface register, no
   * family override (Inter is the page default). Also reused by
   * PricingSectionInner's setup-strip promise line (P3, see file header
   * note): same 18px/600, leading flattened 28->24. */
  tierName: 'text-[18px] leading-[24px] font-semibold',

  /** Price numeral — the largest node on the card. `leading-none` (1) per
   * amendment §5; `tabular-nums` for digit-width stability; `tracking-[-0.02em]`
   * carried over from the current shipped treatment (see file header). */
  price: 'text-[48px] lg:text-[56px] leading-none tracking-[-0.02em] font-semibold tabular-nums',

  /** The unified "16px family" role — tagline, period, VAT label, limits
   * row labels, feature-group row text. Replaces 5 previously-distinct
   * treatments of what read as "the same 16px text" (r5-defect-map.md
   * §4.1). Regular weight — VAT's current `font-semibold` is intentionally
   * dropped here (Decision 10 "one row grammar"), flagged in the mapping
   * table above. Also reused by PricingSectionInner's section subtitle
   * (P3): drops its old lg:text-lg/lg:leading-[1.55] upsize entirely. */
  row: 'text-[16px] leading-[24px] font-normal',

  /** Feature-group category heading ("CRM ȘI LEAD-URI" etc.) — a small
   * uppercase eyebrow-scale label, deliberately smaller than `row` so it
   * reads as a group divider, not body content. */
  groupHeading: 'text-[13px] leading-[18px] font-semibold uppercase tracking-[0.08em]',

  /** Limits-table value (leads/users counts) — "the one deliberate large
   * data emphasis, named as a role" (amendment §5), numbers unchanged from
   * the current shipped treatment. `tabular-nums` so leads/users values
   * keep stable digit width across tiers. */
  limitValue:
    'text-[20px] leading-[24px] lg:text-[24px] lg:leading-[28px] font-semibold tabular-nums',

  /** Fine-print / footnote scale — registered per amendment §5. Consumed
   * by PricingSectionInner's Stripe assurance line (P3, see file header
   * note): a documented 14/20 -> 13/18 shrink. */
  micro: 'text-[13px] leading-[18px] font-normal',

  /** CTA button label — "button system," exempt from a shared line-height
   * (flex-centered, `h-12` fixed button height governs vertical size, not
   * text leading) — matches the current shipped CTA exactly. */
  cta: 'text-[15px] font-semibold',
};
